<!-- Tasks.svelte - Developer task discovery -->
<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";
  import { t } from "./lib/i18n";

  import tasksIcon from "@iconify/icons-mdi/format-list-checks";
  import checkIcon from "@iconify/icons-mdi/check-circle";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import tagIcon from "@iconify/icons-mdi/tag-outline";
  import codeIcon from "@iconify/icons-mdi/code-tags";

  export let apiBase = "";
  export let apiKey = "";

  let tasks = [];
  let loading = true;
  let error = "";

  function headers() {
    const h = { Accept: "application/json" };
    if (apiKey) h.Authorization = `Bearer ${apiKey}`;
    return h;
  }

  async function fetchTasks() {
    loading = true;
    error = "";
    try {
      const res = await fetch(`${apiBase}/tasks`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      tasks = data.tasks || [];
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  onMount(fetchTasks);

  $: availableCount = tasks.filter((task) => task.available).length;
  $: unavailableCount = tasks.length - availableCount;

  const sectionCard =
    "rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 shadow-[0_20px_70px_-60px_rgba(0,0,0,0.9)]";

  const detailCell =
    "rounded-xl border border-white/10 bg-white/[0.055] p-3 min-w-0";

  const detailLabel = "text-xs font-medium uppercase tracking-wide text-zinc-400";

  const mono =
    "font-mono text-xs text-zinc-100 whitespace-nowrap overflow-hidden text-ellipsis";

  const headerChip =
    "rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-zinc-200 inline-flex items-center gap-2";
</script>

<main class="w-full px-3 py-4 sm:px-4 sm:py-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-amber-200/50 bg-amber-400/60 text-amber-50"
        >
          <Icon icon={tasksIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-white">
            {$t('tasks.title')}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-300">
            {$t('tasks.subtitle')}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        {#if !loading && !error}
          <div class={headerChip}>
            <Icon icon={checkIcon} width="16" height="16" class="text-emerald-300" />
            <span>{$t('tasks.availableStats', { available: availableCount, unavailable: unavailableCount, total: tasks.length })}</span>
          </div>
        {/if}

        <button class="ui-btn" on:click={fetchTasks}>
          <Icon icon={refreshIcon} width="16" height="16" />
          <span>{$t('common.refresh')}</span>
        </button>
      </div>
    </div>

    {#if loading}
      <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {#each Array(6) as _}
          <div class="h-56 rounded-2xl border border-white/10 bg-black/20 animate-pulse"></div>
        {/each}
      </div>
    {:else if error}
      <div
        class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <Icon icon={alertIcon} width="18" height="18" />
        <span class="break-words">{$t('tasks.failedToLoad', { error })}</span>
      </div>
    {:else if tasks.length === 0}
      <div class="mt-6 rounded-xl border border-white/10 bg-black/40 p-5 text-sm text-zinc-300">
        <div class="flex items-start gap-3">
          <Icon icon={tasksIcon} width="20" height="20" class="mt-0.5 text-zinc-300" />
          <div>
            <div class="font-medium text-white">{$t('tasks.emptyTitle')}</div>
            <p class="mt-1 max-w-2xl leading-relaxed">
              {$t('tasks.emptyMessage')}
            </p>
          </div>
        </div>
      </div>
    {:else}
      <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {#each tasks as task (task.name)}
          {@const isAvailable = task.available}

          <article class={`${sectionCard} ${isAvailable ? "" : "border-rose-400/20 bg-black/35"}`}>
            <div class="flex items-start justify-between gap-3 border-b border-white/10 pb-3">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  {#if isAvailable}
                    <Icon icon={checkIcon} width="16" height="16" class="shrink-0 text-emerald-300" />
                  {:else}
                    <Icon icon={alertIcon} width="16" height="16" class="shrink-0 text-rose-300" />
                  {/if}
                  <code class="font-mono text-sm font-semibold text-white break-all">{task.name}</code>
                </div>

                {#if task.description}
                  <p class="mt-2 text-sm leading-relaxed text-zinc-300">
                    {task.description}
                  </p>
                {/if}
              </div>

              {#if !isAvailable}
                <span class="shrink-0 rounded-lg border border-rose-400/30 bg-rose-400/10 px-2 py-1 text-[11px] text-rose-200">
                  {$t('tasks.unavailable')}
                </span>
              {/if}
            </div>

            {#if task.human}
              <p class="mt-4 text-sm leading-relaxed text-zinc-200">{task.human}</p>
            {/if}

            <div class="mt-4 grid grid-cols-1 gap-3">
              <div class={detailCell}>
                <div class={detailLabel}>{$t('tasks.handler')}</div>
                <div class={`${mono} mt-2`} title={task.handler || ""}>
                  {task.handler || "-"}
                </div>
              </div>

              {#if task.output_dir}
                <div class={detailCell}>
                  <div class={detailLabel}>{$t('tasks.outputDir')}</div>
                  <div class={`${mono} mt-2`} title={task.output_dir}>
                    {task.output_dir}/
                  </div>
                </div>
              {/if}
            </div>

            {#if task.accept && task.accept.length > 0}
              <div class="mt-4">
                <div class="mb-2 flex items-center gap-2 text-xs text-zinc-500">
                  <Icon icon={tagIcon} width="15" height="15" />
                  <span>{$t('tasks.acceptedInputs')}</span>
                </div>
                <div class="flex flex-wrap gap-1.5">
                  {#each task.accept as ext}
                    <span class="rounded-lg border border-white/10 bg-white/[0.07] px-2 py-1 text-[11px] text-zinc-200">
                      {ext}
                    </span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if task.params && Object.keys(task.params).length > 0}
              <div class="mt-4">
                <div class="mb-2 text-xs text-zinc-500">{$t('tasks.parameters')}</div>
                <div class="grid grid-cols-1 gap-2">
                  {#each Object.entries(task.params) as [key, value]}
                    <div class="rounded-xl border border-white/10 bg-white/[0.055] px-3 py-2 text-xs min-w-0">
                      <span class="text-zinc-400">{key}</span>
                      <span class="ml-2 font-mono text-zinc-100 break-all">{JSON.stringify(value)}</span>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if task.requires && task.requires.length > 0}
              <div class="mt-4">
                <div class="mb-2 text-xs text-zinc-500">{$t('tasks.dependencies')}</div>
                <div class="grid grid-cols-1 gap-2">
                  {#each task.requires as dep}
                    <div class="rounded-xl border border-white/10 bg-white/[0.055] px-3 py-2 text-xs min-w-0">
                      <div class="text-zinc-100">{dep.name}</div>
                      <code class="mt-1 block text-zinc-400 break-all">
                        {dep.type === "binary" ? dep.path : dep.url}
                      </code>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if !isAvailable && task.unavailable_reason}
              <div class="mt-4 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-xs text-rose-100">
                {task.unavailable_reason}
              </div>
            {/if}

            {#if task.cmd}
              <div class="mt-4">
                <div class="mb-2 flex items-center gap-2 text-xs text-zinc-500">
                  <Icon icon={codeIcon} width="15" height="15" />
                  <span>{$t('tasks.command')}</span>
                </div>
                <pre class="overflow-x-auto rounded-xl border border-white/10 bg-black/60 p-3 text-xs leading-relaxed text-zinc-100">{task.cmd}</pre>
              </div>
            {/if}
          </article>
        {/each}
      </div>
    {/if}
  </section>
</main>
